import { Href, useRouter } from "expo-router";
import { useEffect, useRef } from "react";
import { Platform } from "react-native";
import { GMAIL_LINK_QUERY } from "@/services/authServices";
import { showError, showSuccess } from "@/utils/toast";

type UseGmailLinkWebReturnOptions = {
  onSuccess?: () => void;
  onError?: () => void;
  onCancel?: () => void;
};

const DEDUPE_PREFIX = "sparrow_gmail_oauth_dedupe::";

/**
 * After full-page Google OAuth, the server redirects back with ?gmail_link=success|error|cancel.
 * Web-only: show feedback and strip the param from the URL.
 */
export function useGmailLinkWebReturn(
  options: UseGmailLinkWebReturnOptions = {},
) {
  const router = useRouter();
  const routerRef = useRef(router);
  routerRef.current = router;
  const optsRef = useRef(options);
  optsRef.current = options;

  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") {
      return;
    }
    const u0 = new URL(window.location.href);
    const value = u0.searchParams.get(GMAIL_LINK_QUERY);
    if (!value) {
      return;
    }
    // Survives remounts: same return URL may run this effect more than once before URL is clean.
    const dedupeId = `${DEDUPE_PREFIX}${u0.pathname}${u0.search}`;

    const getPathString = (u: URL) =>
      `${u.pathname}${u.search}${u.hash}` || "/";

    /** True if this effect called setItem(dedupeId); must be cleared on unmount if the deferred work never runs. */
    let storageClaimed = false;
    try {
      if (window.sessionStorage.getItem(dedupeId) === "1") {
        u0.searchParams.delete(GMAIL_LINK_QUERY);
        const pathStr = getPathString(u0);
        window.history.replaceState(window.history.state, document.title, pathStr);
        setTimeout(
          () => routerRef.current.replace(pathStr as Href),
          0,
        );
        return;
      }
      window.sessionStorage.setItem(dedupeId, "1");
      storageClaimed = true;
    } catch {
      // private mode, etc.
    }

    u0.searchParams.delete(GMAIL_LINK_QUERY);
    const nextPathStr = getPathString(u0);
    // Strip first so a remounting tree does not read `gmail_link` again.
    window.history.replaceState(
      window.history.state,
      document.title,
      nextPathStr,
    );

    const { onSuccess, onError, onCancel } = optsRef.current;
    const tick = setTimeout(() => {
      try {
        if (value === "success") {
          showSuccess("Gmail linked successfully!");
          onSuccess?.();
        } else if (value === "cancel") {
          onCancel?.();
        } else {
          showError("Failed to link Gmail");
          onError?.();
        }
        routerRef.current.replace(nextPathStr as Href);
      } finally {
        try {
          window.sessionStorage.removeItem(dedupeId);
        } catch {
          // ignore
        }
      }
    }, 0);
    return () => {
      clearTimeout(tick);
      if (storageClaimed) {
        try {
          window.sessionStorage.removeItem(dedupeId);
        } catch {
          // ignore
        }
      }
    };
  }, []);
}
